import {
  Component,
  OnInit,
  HostListener,
  OnDestroy,
  Pipe,
  PipeTransform,
} from "@angular/core";
import { ActivatedRoute, Router, Params } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { JobDetail } from "../../models/jobDetail";
import { Project } from "../../models/project";
import { BehaviorSubject, Subject } from "rxjs";
import { ProjectNameComponent } from "./dialogs/project-name/project-name.component";
import { MatDialog } from "@angular/material/dialog";
import { ProjectDialog } from "../../models/projectDialog";
import { JobDetailComponent } from "./dialogs/job-detail/job-detail.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { JobDetailDialog } from "../../models/jobDetailDialog";
import { AuthService } from "../../shared/services/auth.service";
import { StoredSettingsService } from "../services/stored-settings.service";
import { CachedProjectService } from "../../shared/services/cached-project.service";
import { ElementRef } from "@angular/core";
import {
  distinctUntilKeyChanged,
  filter,
  take,
  takeUntil,
} from "rxjs/operators";
import { KeepJobComponent } from "./dialogs/keep-job/keep-job.component";
import { ProjectService } from "../../shared/services/project.service";
import { Job } from "../../models/job";
import { NzNotificationService } from "ng-zorro-antd/notification";

interface JobItem {
  id: string;
  job__id: string;
  job__job_name: string;
  job__job_basepath: string;
}

interface GroupItem {
  id: string;
  name: string;
  project_name: string;
  created_by_name: string;
  comment: string;
  jobs: JobItem[];
}

declare var $: any;
@Component({
    selector: "app-project",
    templateUrl: "./project.component.html",
    styleUrls: ["./project.component.less"],
    standalone: false
})
export class ProjectComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private i_currentJobSource = new BehaviorSubject<any>(null);
  i_currentJob: JobDetail;
  i_popularIndex: number;

  jobs: any[];
  popularJobs: any[] = [];
  popularJob_ids = new Set();

  project: Project;
  tabBarVisible: boolean = false;
  tabIsEmpty: boolean = true;
  projectSettingsBarBottom = "53px";
  windowWidth: number;
  isMobile: boolean;
  isReloadingJob: boolean = false;
  jobComments: Map<string, string> = new Map<string, string>();
  jobGroups: GroupItem[] = [];
  synchroniseSettings = false;
  parentJobFullStr: string = null;
  parentJobDisplayStr: string = null;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private projectService: CachedProjectService,
    private uncachedProjectService: ProjectService,
    private router: Router,
    private storedSettingsService: StoredSettingsService,
    public authService: AuthService,
    private elementRef: ElementRef,
    private titleService: Title,
    private notificationService: NzNotificationService
  ) {}

  ngOnInit() {
    this.windowWidth = window.innerWidth;
    this.isMobile = /Mobile/.test(navigator.userAgent);
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Params) => {
        if (!params["id"]) {
          console.warn("No project id provided");
          return;
        }
        if (!this.jobs) {
          this.getJobsFromParams(params);
        } else {
          let id = params["id"];
          let selectedIndex = this.jobs.findIndex((j) => j.id === id);
          this.i_currentJobSource.next(this.jobs[selectedIndex]);
        }
      });

    this.storedSettingsService.synchroniseSettings
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => (this.synchroniseSettings = val));

    this.i_currentJobSource
      .pipe(
        takeUntil(this.destroy$),
        filter((j) => !!j),
        // Only emit if the ID actually changed
        // This kills the "double-fire" from the two tab groups
        distinctUntilKeyChanged("id"),
      )
      .subscribe((job) => {
        if (job) {
          this.i_popularIndex = this.popularJobs.findIndex(
            (j) => j.id === job.id,
          );
          this.onHandleJobChange(job.id);
        }
      });
  }

  onHandleJobChange(id: string) {
    this.projectService
      .getJobDetail(id)
      .pipe(take(1))
      .subscribe((p) => {
        this.i_currentJob = p;
        this.titleService.setTitle(this.i_currentJob.projectName);
        this.updateUrl(this.i_currentJob.id);
        this.setParentJobDisplayStr();
      });
  }

  compareFn = (o1: any, o2: any): boolean =>
    o1 && o2 ? o1.id === o2.id : o1 === o2;

  modifySliceBottom() {
    let slider = this.elementRef.nativeElement.querySelector(".sliders-bar");
    if (slider) {
      slider.style.bottom = this.tabIsEmpty ? "53px" : "108px";
    }
    this.projectSettingsBarBottom = this.tabIsEmpty ? "53px" : "108px";
  }

  /**
   *
   * @description add current job to popular tab
   */
  addTab() {
    if (this.popularJob_ids.has(this.i_currentJob.id)) {
      return;
    }

    this.popularJobs.push(this.i_currentJob);
    this.popularJob_ids.add(this.i_currentJob.id);
    this.i_popularIndex = this.popularJobs.length - 1;
    this.tabIsEmpty = this.popularJobs.length == 0;
    this.modifySliceBottom();
  }

  /**
   *
   * @description remove current job from popular tab
   */
  removeTab() {
    this.popularJob_ids.delete(this.i_currentJob.id);
    this.popularJobs = this.popularJobs.filter(
      (j) => j.id !== this.i_currentJob.id,
    );
    this.tabIsEmpty = this.popularJobs.length == 0;
    this.i_popularIndex = -1;
    this.modifySliceBottom();
  }

  shiftLeft() {
    if (this.i_popularIndex <= 0) {
      return;
    }
    const newArr = [...this.popularJobs];
    [newArr[this.i_popularIndex], newArr[this.i_popularIndex - 1]] = [
      newArr[this.i_popularIndex - 1],
      newArr[this.i_popularIndex],
    ];
    this.popularJobs = newArr;
    this.i_popularIndex -= 1;
  }

  shiftRight() {
    if (
      this.i_popularIndex < 0 ||
      this.i_popularIndex >= this.popularJobs.length - 1
    ) {
      return;
    }
    const newArr = [...this.popularJobs];
    [newArr[this.i_popularIndex], newArr[this.i_popularIndex + 1]] = [
      newArr[this.i_popularIndex + 1],
      newArr[this.i_popularIndex],
    ];
    this.popularJobs = newArr;
    this.i_popularIndex += 1;
  }

  ngOnDestroy() {
    this.titleService.setTitle("S-Cube Cloud");
  }

  @HostListener("window:scroll", ["$event"])
  onWindowScroll(e) {
    var offset = $("#tab-bar").offset();
    this.tabBarVisible = offset && offset.top + 50 < $(document).scrollTop();
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize(e) {
    this.windowWidth = window.innerWidth;
  }

  onSynchroniseChange(value) {
    this.storedSettingsService.setSynchroniseSettings(value);
  }

  onIndexChangedHandler(index: number, type: "all" | "fav") {
    var selectedJob: Job;
    const list = type === "fav" ? this.popularJobs : this.jobs;
    selectedJob = list[index];
    if (selectedJob) {
      this.i_currentJobSource.next(selectedJob);
    }
  }

  updateUrl(jobid: string) {
    const currentUrl = this.router.url;
    // targetting and changing specifically and only the jobId section of the url while preserving everything else
    const re = new RegExp(/(\/projects\/)[^\/]+/);
    const nextUrl = currentUrl.replace(re, `$1${jobid}`);
    this.router.navigateByUrl(nextUrl);
  }

  openProjectDialog() {
    let dialogRef = this.dialog.open(ProjectNameComponent, {
      width: "400px",
      data: {
        id: this.project.id,
        name: this.i_currentJob.projectName,
        description: this.i_currentJob.projectDescription,
        field: this.project.field,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ProjectDialog) => {
        if (!result) return;
        this.projectService
          .updateProjectDetails(result)
          .pipe(take(1))
          .subscribe(
            (data) => {
              this.project.name = result.name;
              this.project.description = result.description;
              this.project.field = result.field || null;
              this.i_currentJob.projectName = result.name;
              this.i_currentJob.projectDescription = result.description;

              this.projectService.clearProjectsCache();
              this.projectService.clearJobDetailCache(
                this.project.jobs.map((j) => j.id),
              );
              this.snackBar.open("Project details updated", null, {
                duration: 2000,
              });
            },
            (error) => {
              this.snackBar.open(
                "Sorry, the project details could not be updated",
                null,
                { duration: 2000 },
              );
            },
          );
      });
  }

  openJobDialog(isPopular: boolean) {
    var job = this.i_currentJob;
    if (!job) return;
    // refresh job details before open job dialog
    this.projectService
      .refreshJobDetails(job.id)
      .pipe(take(1))
      .subscribe(
        (refresh) => {
          this.projectService.clearJobDetailCache([job.id]);
          this.project = refresh.project;
          this.i_currentJob.name = refresh.job_name;
          this.i_currentJob.basePath = refresh.job_basepath;
          this.i_currentJob.iterations = refresh.iterations;
          this.i_currentJob.iterationsComplete = refresh.iterations_completed;
          this.i_currentJob.status = refresh.status;
          this.i_currentJob.comments = refresh.comments;
          this.i_currentJob.tag = refresh.tag;
          job.name = refresh.job_name;
          job.status = refresh.status;

          // open job dialog
          let dialogRef = this.dialog.open(JobDetailComponent, {
            // height: "500px",
            width: "400px",
            data: {
              id: job.id,
              name: job.name,
              projectId: this.project.id,
              basePath: this.i_currentJob.basePath,
              iterations: this.i_currentJob.iterations,
              iterationsComplete: this.i_currentJob.iterationsComplete,
              status: this.i_currentJob.status,
              comments: this.i_currentJob.comments,
              tag: this.i_currentJob.tag,
              parent_job_id: this.i_currentJob.parent_job_id,
              parent_cp_num: this.i_currentJob.parent_cp_num,
              offshoot: this.i_currentJob.offshoot,
              x2_is_inline: this.i_currentJob.x2_is_inline,
              owner: refresh.owner,
            },
          });

          dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result: JobDetailDialog | { isReparse: boolean }) => {
              if (!result) return;

              if (
                result.hasOwnProperty("isReparse") &&
                (result as any).isReparse
              ) {
                // Clear cache
                this.projectService.clearProjectsCache();
                this.projectService.clearJobDetailCache(
                  this.project.jobs.map((j) => j.id),
                );

                // Refresh job details from backend
                this.projectService
                  .refreshJobDetails(this.i_currentJob.id as any)
                  .pipe(take(1))
                  .subscribe(
                    (refreshedJobDetail) => {
                      // Update local job detail with fresh data
                      this.i_currentJob.name = refreshedJobDetail.job_name;
                      this.i_currentJob.basePath =
                        refreshedJobDetail.job_basepath;
                      this.i_currentJob.iterations =
                        refreshedJobDetail.iterations;
                      this.i_currentJob.iterationsComplete =
                        refreshedJobDetail.iterations_completed;
                      this.i_currentJob.status = refreshedJobDetail.status;
                      this.i_currentJob.comments = refreshedJobDetail.comments;
                      this.i_currentJob.tag = refreshedJobDetail.tag;

                      // update the job in jobs array
                      const job = this.jobs.find(
                        (j) => j.id === this.i_currentJob.id,
                      );
                      if (job) {
                        job.name = refreshedJobDetail.job_name;
                        job.status = refreshedJobDetail.status;
                        job.iterations = refreshedJobDetail.iterations;
                        job.iterationsComplete =
                          refreshedJobDetail.iterations_completed;
                        job.comments = refreshedJobDetail.comments;
                        job.tag = refreshedJobDetail.tag;
                        job.basePath = refreshedJobDetail.job_basepath;
                        job.parent_job_id = refreshedJobDetail.parent_job_id;
                        job.parent_cp_num = refreshedJobDetail.parent_cp_num;
                      }
                    },
                    (error) => {
                      this.snackBar.open(
                        "Job re-parsed but failed to refresh data",
                        null,
                        { duration: 2000 },
                      );
                    },
                  );
                return;
              }

              const jobResult = result as JobDetailDialog;
              this.projectService
                .updateJobDetails(jobResult)
                .pipe(take(1))
                .subscribe(
                  (data) => {
                    this.i_currentJob.name = jobResult.name;
                    this.i_currentJob.basePath = jobResult.basePath;
                    this.i_currentJob.iterations = jobResult.iterations;
                    this.i_currentJob.iterationsComplete =
                      jobResult.iterationsComplete;
                    this.i_currentJob.status = jobResult.status;
                    this.i_currentJob.comments = jobResult.comments;
                    this.i_currentJob.parent_job_id = jobResult.parent_job_id;
                    this.i_currentJob.parent_cp_num = jobResult.parent_cp_num;
                    this.i_currentJob.offshoot = jobResult.offshoot;
                    job.name = jobResult.name;
                    job.status = jobResult.status;

                    this.projectService.clearProjectsCache();
                    this.projectService.clearJobDetailCache(
                      this.project.jobs.map((j) => j.id),
                    );
                    this.snackBar.open("Job Details updated", null, {
                      duration: 2000,
                    });

                    if (jobResult.projectId !== this.project.id) {
                      this.router.navigate(["/projects"]);
                      return;
                    }
                  },
                  (error) => {
                    this.snackBar.open(
                      "Sorry, the job details could not be updated",
                      null,
                      { duration: 2000 },
                    );
                  },
                );
            });
        },
        (error) => {
          this.snackBar.open(
            "Sorry, the job details could not be updated",
            null,
            { duration: 2000 },
          );
        },
      );
  }

  get isCompactView(): boolean {
    return this.isMobile || window.innerWidth < 1200;
  }

  async getJobsFromParams(params: Params, forceRefresh: boolean = false) {
    let id = params["id"];
    let snapShotParams = this.route.snapshot.queryParams;
    // scenario of specific jobs requested
    if (snapShotParams.jobs && snapShotParams.jobs.length > 0) {
      try {
        const response = await this.projectService.getMultipleJobData(snapShotParams.jobs, forceRefresh).toPromise();
        this.jobs = response;
        let selectedIndex = this.jobs.findIndex((j) => j.id === id);
        this.i_currentJobSource.next(this.jobs[selectedIndex]);
      } catch (error) {
        this.notificationService.error("Error fetching job data", error)
      }
    } else {
      try {
        const project: Project = await this.projectService.getJobProject(id, forceRefresh).toPromise();
        if (project) {
          this.project = project;
          this.jobs = project.jobs;
          let selectedIndex = this.jobs.findIndex((j) => j.id === id);
          this.i_currentJobSource.next(this.jobs[selectedIndex]);
        } else {
          // project not found, probably time to redirect to a page to say unauthorized and talk to admin
          this.router.navigate(["/contact-admin"]);
        }
      } catch (error) {
        this.notificationService.error("Error fetching project data", error)
      }
    }
  }

  /**
   *
   * @description refresh jobs when user clicks on the refresh button, does not use the local cache service,
   * not sure about other cache service.
   */
  async refreshJobs() {
    this.isReloadingJob = false
    const params: Params = await this.route.params.pipe(take(1)).toPromise();
    this.getJobsFromParams(params, true).then(() => this.notificationService.success('Jobs refreshed!','')).finally(() => this.isReloadingJob = false);
  }

  /**
   *
   * @description opens a dialog to ask user to confirm if they want to keep / not keep the job
   */
  keep_job() {
    let name = this.i_currentJob.name;
    let keep_job = this.i_currentJob.keep_job;
    this.dialog
      .open(KeepJobComponent, {
        height: "320px",
        width: "400px",
        data: {
          job_name: name,
          keep_job: keep_job,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result != undefined && result["confirm"]) {
          keep_job = !keep_job;
          this.update_entry(this.i_currentJob.id, keep_job);
        }
      });
  }

  /**
   *
   * @param job_id
   * @param keep_job
   * @description update the keep_job field in the database
   */
  update_entry(job_id, keep_job) {
    this.projectService
      ._getJobDetail(job_id)
      .toPromise()
      .then((data) => {
        let jobDetailDialog: JobDetailDialog = {
          id: data["id"],
          name: data["name"],
          projectId: data["projectId"],
          basePath: data["basePath"],
          iterations: data["iterations"],
          iterationsComplete: data["iterationsComplete"],
          status: data["status"],
          comments: data["comments"],
          tag: data["tag"],
          parent_job_id: data["parent_job_id"],
          parent_cp_num: data["parent_cp_num"],
          keep_job: keep_job,
          x2_is_inline: data["x2_is_inline"],
          owner: data["owner"],
        };
        this.projectService
          .updateJobDetails(jobDetailDialog)
          .toPromise()
          .then((data) => {
            this.i_currentJob.keep_job = keep_job;
          });
      });
  }

  navigateToProjectTable() {
    window.open(
      "/project/" + this.i_currentJob.projectId + "/parameter-view",
      "_blank",
    );
  }

  getJobGroups() {
    this.uncachedProjectService
      .getJobGroups(this.project.id)
      .toPromise()
      .then((res) => {
        this.jobGroups = res.data;
      })
      .catch((err) => {
        console.error(err);
      });
  }

  onGroupClick(group: GroupItem) {
    let redirection_url = `projects/${group.jobs[0].job__id}/model?${group.jobs
      .map((job) => `jobs=${job.job__id}`)
      .join("&")}`;

    window.open(redirection_url, "_self");
  }

  /**
   *
   * @description calculate the number of days left before the job is archived if the job is not marked kept
   * might not be entirely accurate due to time zone difference.
   * @returns number of days left
   */
  getJobCleanUpCountDown() {
    let now = new Date();
    let createdDate = new Date(this.i_currentJob.createdDate);
    let sevendays = 7 * 24 * 60 * 60 * 1000;
    let cleanupdate = new Date(createdDate.getTime() + sevendays);
    let diff = cleanupdate.getTime() - now.getTime();
    let days = 0;
    if (diff > 0) {
      days = Math.floor(diff / (1000 * 60 * 60 * 24));
    }
    return days;
  }

  /**
   *
   * @description based on values in the jobDetail object, determine the archive status of the job
   * @returns string representing the archive status
   */
  getJobArchiveStatus() {
    let job = this.i_currentJob;
    let created = new Date(this.i_currentJob.createdDate);
    if (job.keep_job) {
      return "keep";
    } else if (
      created.getTime() + 7 * 24 * 60 * 60 * 1000 >
      new Date().getTime()
    ) {
      return "< 7 days (full storage)";
    } else if (job.soft_archive_status == true) {
      return "glacier instant retrieval";
    } else if (job.is_archived == true) {
      return "glacier deep archive";
    } else {
      return "full storage";
    }
  }

  /**
   *
   * @description if job has parent, form a string that displays the parent job name and cp number
   * assume the parent job is in the same project
   */
  setParentJobDisplayStr() {
    if (
      this.i_currentJob.parent_job_id != null &&
      this.i_currentJob.parent_cp_num != null
    ) {
      let parent_job = this.jobs.find(
        (job) => job.id == this.i_currentJob.parent_job_id,
      );
      if (parent_job != null) {
        this.parentJobDisplayStr =
          parent_job.name.substring(0, 10) +
          "-CP" +
          this.i_currentJob.parent_cp_num.toString();
        if (this.i_currentJob.offshoot == true) {
          this.parentJobDisplayStr = this.parentJobDisplayStr + " (offshoot)";
        }
        this.parentJobFullStr = parent_job.name;
      } else {
        this.parentJobDisplayStr = null;
        this.parentJobFullStr = null;
      }
    } else {
      this.parentJobDisplayStr = null;
      this.parentJobFullStr = null;
    }
  }

  /**
   *
   * @description determine if we need to show the countdown for the job
   * @returns boolean
   */
  showCountDown() {
    return this.getJobArchiveStatus() == "< 7 days (full storage)";
  }

  /**
   * Check if the currently selected job is in the popular jobs list
   * @returns boolean - true if current job is in popular jobs, false otherwise
   */
  isCurrentJobInPopularJobs(): boolean {
    return this.popularJob_ids.has(this.i_currentJob.id);
  }

  trackByJobId(index: number, item: any) {
    return item?.id;
  }
}

@Pipe({
    name: "findJobIndex",
    pure: true,
    standalone: false
})
export class FindJobIndexPipe implements PipeTransform {
  transform(list: any[], jobId: string): number {
    let index = list.findIndex((job) => job.id === jobId);
    return index >= 0 ? index : undefined;
  }
}
